// Vendor
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { sortList } from 'utils/sortList';
import { createURL } from 'utils/url';

// Custom Hooks
import useTheme from 'hooks/useTheme';
import usePromos from 'hooks/usePromos';

// Filters
import { sortTypes, defaultCategories } from './data/filters.json';

// Components
import Page from 'components/Page';
import Container from 'components/Container';
import Heading from 'components/Heading';
import SelectField from 'components/SelectField';
import Vendors from 'components/pages/Vendors';
import VendorsSidebar from 'components/pages/Vendors/VendorsSidebar';

import styles from './styles.styl';
import { loadPartnersIfNotLoaded } from 'state/partners/actions';

const EMPTY_CONTENT = [];
const REQURED_PROMOS = {
  header: { 'big-image': 1 },
  sidebar: { image: 2 },
  footer: { image: 2 },
};

function PartnersPage({ router, categories, vendors, partners, promo, meta }) {
  const query = router.query;
  const promos = promo.sidebar;
  const categoriesList = useMemo(() => {
    const options = [...defaultCategories, ...categories];

    return options;
  }, [categories]);
  const listRef = useRef();
  const [sortedVendors, setVendors] = useState(vendors);
  const page = useMemo(() => (query.page ? Number(query.page) : 1), [query.page]);
  const searchValue = useMemo(() => query.title || '', [query.title]);
  const sort = useMemo(() => query.sort || sortTypes[0].value, [query.sort]);
  const category = useMemo(() => query.category || categoriesList[0].value, [
    categoriesList,
    query.category,
  ]);

  const setTheme = useTheme().setTheme;

  const setPaginationHref = useCallback(
    page => `/partners?page=${page}&title=${searchValue}&sort=${sort}&category=${category}`,
    [category, searchValue, sort],
  );

  // Смена cтраницы
  const changePage = useCallback(
    newPage => {
      router.push(
        createURL('/partners', {
          page: newPage,
          title: searchValue,
          sort,
          category,
        }),
        `/partners`,
      );
    },
    [category, router, searchValue, sort],
  );

  // Смена категории
  const onChangeCategory = useCallback(
    newCategory => {
      router.push(
        createURL('/partners', {
          title: searchValue,
          sort,
          category: newCategory,
        }),
        `/partners`,
      );
    },
    [router, searchValue, sort],
  );

  // Смена сортировки
  const onChangeSelect = useCallback(
    newSort => {
      router.push(
        createURL('/partners', {
          title: searchValue,
          sort: newSort,
          category,
        }),
        `/partners`,
      );
    },
    [category, router, searchValue],
  );

  // Поиск по имени
  const onSearch = useCallback(
    value => {
      const { search } = value;
      const query = search || '';

      router.push(
        createURL('/partners', {
          title: query,
          sort,
          category,
        }),
        `/partners`,
      );
    },
    [category, router, sort],
  );

  // Смена фильтров
  useEffect(() => {
    const isCategory = categories.find(item => item.value === category);

    sortList({}, '/partners', 'vendors', setVendors, {
      title: searchValue,
      sort,
      [isCategory ? 'category' : 'tag']: category,
      offset: (page - 1) * 5,
    });
  }, [page, category, sort, searchValue, categories]);

  // Смена темы
  useEffect(() => {
    setTheme({ theme: 'default', bgImage: null, bgColor: null });
  }, [setTheme]);

  usePromos(promo, EMPTY_CONTENT, REQURED_PROMOS);

  const title = (vendors && vendors.title) || 'Partners';

  return (
    <Page className={styles.PartnersPage} meta={meta} title="Partners">
      <Container>
        <Page.Section className={styles.PartnersPage__calendarSection}>
          <div className={styles.PartnersPage__header}>
            <Heading className={styles.PartnersPage__title} level={1}>
              {title}
            </Heading>
            <SelectField
              className={styles.PartnersPage__filter}
              options={categoriesList}
              value={category}
              onChange={onChangeCategory}
              theme="inArticles"
            />
          </div>
          <section className={styles.PartnersPage__inner}>
            <div className={styles.PartnersPage__body}>
              <Vendors
                ref={listRef}
                page={page}
                loadPage={changePage}
                vendors={sortedVendors}
                sortType={sort}
                sortTypes={sortTypes}
                onChangeSelect={onChangeSelect}
                onChangeCategory={onChangeCategory}
                query={searchValue}
                onSearch={onSearch}
                setPaginationHref={setPaginationHref}
                url={'/partners'}
              />
            </div>

            <VendorsSidebar promos={promos} partners={partners} />
          </section>
        </Page.Section>
      </Container>
    </Page>
  );
}

PartnersPage.getInitialProps = async ({ store, query }) => {
  return store.dispatch(loadPartnersIfNotLoaded(query)).then(() => ({}));
};

const connector = connect(({ partners }) => {
  return partners.data;
});

PartnersPage.propTypes = {
  router: PropTypes.object.isRequired,
  categories: PropTypes.array,
  vendors: PropTypes.object,
  promo: PropTypes.object,
  partners: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  meta: PropTypes.object,
};

export default withRouter(connector(PartnersPage));
